import React, { useState } from 'react'
import Modal from './Modal'
import { Field, inputClass } from './Field'
import { BalanceAdjustment } from '../types'
import { makeId } from '../lib/id'
import { toISODate } from '../lib/dateRules'

interface Props {
  initial?: BalanceAdjustment
  onSave: (item: BalanceAdjustment, addAnother: boolean) => void
  onDelete?: () => void
  onClose: () => void
}

export default function AdjustmentForm({ initial, onSave, onDelete, onClose }: Props) {
  const [date, setDate] = useState(initial?.date ?? toISODate(new Date()))
  const [amount, setAmount] = useState(initial ? Math.abs(initial.delta).toString() : '')
  const [direction, setDirection] = useState<'ahead' | 'behind'>(
    initial && initial.delta < 0 ? 'behind' : 'ahead',
  )
  const [note, setNote] = useState(initial?.note ?? '')
  const [active, setActive] = useState(initial?.active ?? true)

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null
    const addAnother = submitter?.dataset.action === 'add-another'
    const delta = Math.abs(parseFloat(amount) || 0) * (direction === 'behind' ? -1 : 1)
    const item: BalanceAdjustment = {
      id: initial?.id ?? makeId(),
      kind: 'adjustment',
      date,
      delta,
      note: note.trim() || undefined,
      active,
    }
    onSave(item, addAnother)
  }

  return (
    <Modal title={`${initial ? 'Edit' : 'Add'} balance adjustment`} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <p className="text-xs text-ink/70 mb-4">
          Reconcile against what actually happened — e.g. you overspent, or found you had more left
          than planned. It acts like a new starting balance from this date forward.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Date">
            <input className={inputClass} type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </Field>
          <Field label="Amount">
            <input
              className={inputClass}
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </Field>
        </div>

        <Field label="Direction">
          <select className={inputClass} value={direction} onChange={(e) => setDirection(e.target.value as 'ahead' | 'behind')}>
            <option value="behind">Behind plan (overspent / less than expected)</option>
            <option value="ahead">Ahead of plan (underspent / more than expected)</option>
          </select>
        </Field>

        <Field label="Reason (optional)" hint="Context for why the deviation happened — not an excuse, just the story.">
          <textarea
            className={inputClass}
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Overspent on groceries, ran out of shampoo, conditioner, toothpaste, cleaning products, and had a small party"
          />
        </Field>

        <label className="flex items-center gap-2 text-sm text-ink mb-5">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active
        </label>

        <div className="flex items-center justify-between pt-3 border-t border-line mt-2">
          <div>
            {onDelete && (
              <button type="button" onClick={onDelete} className="text-sm text-rag-bad hover:underline">
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-sm border border-line text-ink/80 hover:bg-paper">
              Cancel
            </button>
            {!initial && (
              <button
                type="submit"
                data-action="add-another"
                className="px-3 py-1.5 text-sm rounded-sm border border-accent text-accent hover:bg-accent/10"
              >
                Save &amp; add another
              </button>
            )}
            <button type="submit" data-action="save" className="px-3 py-1.5 text-sm rounded-sm bg-accent text-white hover:bg-accent/90">
              Save
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
